import type { DayPoint, HourPoint, WeatherReport } from '../../shared/contract'
import { localDate, formatter } from '../../shared/units'

// ─── Hourly strip ─────────────────────────────────────────────────────────────

export type HourlyItem =
  | { kind: 'hour'; key: string; time: string; /** The instant the column is labelled with: `time` rounded to the nearest local hour. */ labelTime: string; isNow: boolean; hour: HourPoint }
  | { kind: 'sunrise' | 'sunset'; key: string; time: string }

const HOUR = 3_600_000

/**
 * Index of the hour the series is "in" at `now`: the last slot that started at or before it
 * (slots are one hour long). A report is often older than the page showing it (a persisted or
 * edge-cached one), so its first hour is not necessarily the current one. Before the first slot,
 * or when the whole series is already past (nothing sensible to skip to), it is 0.
 */
export function currentHourIndex(hours: readonly { time: string }[], now: number): number {
  const n = hours.length
  if (!n) return 0
  if (now >= Date.parse(hours[n - 1].time) + HOUR) return 0
  let idx = 0
  for (let i = 0; i < n; i++) {
    if (Date.parse(hours[i].time) <= now) idx = i
    else break
  }
  // A gap in the series: the slot at `idx` ended already, so the next one is the nearest to now.
  if (idx + 1 < n && now >= Date.parse(hours[idx].time) + HOUR) idx++
  return idx
}

/**
 * The instant to print for a column: the slot's start rounded to the nearest local hour, so a
 * zone on a half-hour offset (Asia/Kolkata, Lord Howe in winter) reads "9 PM, 10 PM" rather than
 * repeating the hour of the column the clock is in. Whole-hour zones are untouched.
 */
export function hourLabelTime(iso: string, timeZone: string): string {
  const frac = localHourFraction(iso, timeZone)
  const minute = Math.round((frac - Math.floor(frac)) * 60)
  if (minute === 0 || minute === 60) return iso
  const delta = minute >= 30 ? 60 - minute : -minute
  return new Date(Date.parse(iso) + delta * 60_000).toISOString()
}

/**
 * The hourly strip: the current hour ("Now") + the next `count` hours, with sunrise/sunset events
 * inserted between the hours they fall in (Apple-style). Hours before the current one are
 * dropped, so "Now" is always the hour the clock is in and the next column is the next hour.
 * Events before the first hour or after the last, and ones that have already happened, are
 * dropped so the strip stays chronological.
 */
export function buildHourlyItems(
  hourly: HourPoint[],
  daily: Pick<DayPoint, 'sunrise' | 'sunset'>[],
  count = 24,
  now: number = Date.now(),
  timeZone = 'UTC',
): HourlyItem[] {
  if (!hourly.length) return []
  const first = currentHourIndex(hourly, now)
  const hours = hourly.slice(first, first + count + 1)
  const start = Date.parse(hours[0].time)
  const end = Date.parse(hours[hours.length - 1].time)
  // Only an event still to come is shown; if the clock is outside the series, the series decides.
  const after = now > start && now < end + HOUR ? now : start
  const events: { kind: 'sunrise' | 'sunset'; time: string; t: number }[] = []
  for (const d of daily) {
    for (const kind of ['sunrise', 'sunset'] as const) {
      const iso = d[kind]
      if (!iso) continue
      const t = Date.parse(iso)
      if (Number.isFinite(t) && t > start && t > after && t < end) events.push({ kind, time: iso, t })
    }
  }
  events.sort((a, b) => a.t - b.t)
  const items: HourlyItem[] = []
  let e = 0
  hours.forEach((h, i) => {
    const t = Date.parse(h.time)
    while (e < events.length && events[e].t <= t) {
      items.push({ kind: events[e].kind, key: `${events[e].kind}-${events[e].time}`, time: events[e].time })
      e++
    }
    items.push({ kind: 'hour', key: h.time, time: h.time, labelTime: hourLabelTime(h.time, timeZone), isNow: i === 0, hour: h })
  })
  return items
}

// ─── 10-day range bars ────────────────────────────────────────────────────────

export interface RangeBar {
  /** Percent from the left of the track. */
  left: number
  /** Percent width, with a small minimum so a one-degree day still shows a capsule. */
  width: number
}

export function weekExtent(days: Pick<DayPoint, 'low_c' | 'high_c'>[], nowC?: number | null): [number, number] | null {
  const vals: number[] = []
  for (const d of days) {
    if (d.low_c != null) vals.push(d.low_c)
    if (d.high_c != null) vals.push(d.high_c)
  }
  if (nowC != null) vals.push(nowC)
  if (!vals.length) return null
  return [Math.min(...vals), Math.max(...vals)]
}

export function rangeBar(min: number, max: number, low: number, high: number, minWidth = 4): RangeBar {
  const span = max - min
  if (!(span > 0)) return { left: 0, width: 100 }
  const lo = Math.min(low, high)
  const hi = Math.max(low, high)
  let left = ((lo - min) / span) * 100
  let width = ((hi - lo) / span) * 100
  left = clamp(left, 0, 100)
  width = clamp(width, 0, 100 - left)
  if (width < minWidth) {
    const center = left + width / 2
    width = Math.min(minWidth, 100)
    left = clamp(center - width / 2, 0, 100 - width)
  }
  return { left: round2(left), width: round2(width) }
}

/** Percent position of a value along the week track (for the "now" dot on Today). */
export function rangePosition(min: number, max: number, value: number): number {
  const span = max - min
  if (!(span > 0)) return 50
  return round2(clamp(((value - min) / span) * 100, 0, 100))
}

// ─── Day helpers ──────────────────────────────────────────────────────────────

/** Hours of the hourly series that fall on a local calendar date. */
export function hoursForDate(report: WeatherReport, date: string): HourPoint[] {
  const tz = report.location.timezone
  return report.hourly.filter((h) => localDate(h.time, tz) === date)
}

/**
 * Today's high/low. The engine already folds the observed-so-far extremes into
 * daily[0] (adjusted from the station's elevation to this location), so every surface —
 * app, widgets, macOS widget — shows the same pair. `observed` stays informational.
 */
export function todayHighLow(report: WeatherReport): { high: number | null; low: number | null } {
  const d = report.daily[0]
  return d ? { high: d.high_c, low: d.low_c } : { high: null, low: null }
}

/** UV ≥ 3 window for the rest of today, "10 AM–4 PM". */
export function uvWindow(report: WeatherReport): { start: string; end: string } | null {
  const today = report.daily[0]?.date
  if (!today) return null
  const hrs = hoursForDate(report, today).filter((h) => (h.uv_index ?? 0) >= 3)
  if (!hrs.length) return null
  const endT = new Date(Date.parse(hrs[hrs.length - 1].time) + 3600_000).toISOString()
  return { start: hrs[0].time, end: endT }
}

/** Whether next-hour precipitation is worth a card (precip within ~2 h). */
export function showNextHour(report: WeatherReport): boolean {
  const m = report.minutely
  if (m.status === 'unavailable' || !m.points.length) return false
  if (m.status !== 'dry') return true
  return m.points.some((p) => p.intensity_mmh > 0.05 && p.probability >= 30)
}

// ─── misc ─────────────────────────────────────────────────────────────────────

export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v))
}

function round2(v: number) {
  return Math.round(v * 100) / 100
}

/** Ordinal-free short date, "Sep 26", for a local YYYY-MM-DD. */
export function shortDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  return formatter('UTC', { month: 'short', day: 'numeric' }).format(
    new Date(Date.UTC(y, m - 1, d, 12)),
  )
}

export function longWeekday(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  return formatter('UTC', { weekday: 'long' }).format(new Date(Date.UTC(y, m - 1, d, 12)))
}

export function dayOfMonth(date: string): number {
  return Number(date.split('-')[2])
}

export function weekdayLetter(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  return formatter('UTC', { weekday: 'narrow' }).format(new Date(Date.UTC(y, m - 1, d, 12)))
}

/** Local fractional hour (0–24) of an instant at the location. */
export function localHourFraction(iso: string, timeZone: string): number {
  const parts = formatter(timeZone, {
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso))
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0)
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0)
  return h + m / 60
}

// The detail sheets' day window (midnight-to-midnight, DST-true hours) lives in dayWindow.ts, which only
// the lazy sheets chunk loads.

/** "in 3 days" style relative day count. */
/**
 * Calendar days from now until an instant, counted in the place's time zone — so "today" /
 * "in 1 day" always agree with the date shown beside them.
 */
export function daysUntil(iso: string, now = Date.now(), timeZone = 'UTC'): number {
  const day = (ms: number) => {
    const [y, m, d] = localDate(new Date(ms).toISOString(), timeZone).split('-').map(Number)
    return Date.UTC(y, m - 1, d)
  }
  return Math.max(0, Math.round((day(Date.parse(iso)) - day(now)) / 86_400_000))
}

/** Human duration "12 hr 13 min". */
export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '–'
  const s = Math.abs(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.round((s % 3600) / 60)
  if (h === 0) return `${m} min`
  return `${h} hr ${m} min`
}
